import { type ReactElement, startTransition } from 'react';
// eslint-disable-next-line jira/restricted/react-18
import { createRoot, hydrateRoot } from 'react-dom/client';
import { enableHydrationStatReporting } from '@jira/hydration-stats-reporting/src/ui/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { setIsHydrationRenderMode } from '@atlassian/jira-ssr-controller/src/use-is-page-hydrating.tsx';
import { initDevHydrationReporting } from './hydration-reporting.tsx';
import { hydrationOptions } from './hydration-options.tsx';
import { installHydrationDebugger } from './hydration-debugger/install-debugger.tsx';

export const getIsSsrHydrationEnabled = () => {
	// We want only 1 source of truth for "should hydrate" (coming from the server),
	// but keep the fg('jfp-magma-force-react-hydrate-root') here for local dev convenience.
	return (
		(typeof window !== 'undefined' && window.SPA_STATE?.clientShouldHydrate) ||
		fg('jfp-magma-force-react-hydrate-root')
	);
};

export const renderReactApp = (
	children: ReactElement,
	{
		isSsrHydrationEnabled,
		container,
	}: {
		isSsrHydrationEnabled: boolean;
		/**
		 * The container to render the React app into.
		 */
		container: HTMLElement;
	},
) => {
	if (isSsrHydrationEnabled) {
		setIsHydrationRenderMode();
		activateReactPatches();
		if (fg('jfp-magma-ssr-hydration-debugger')) {
			installHydrationDebugger(container);
		}

		if (process.env.NODE_ENV === 'development') {
			// At least for now, this only has an effect in dev mode (due to our patch only being active in dev mode)
			initDevHydrationReporting(window);
		}
		enableHydrationStatReporting();

		if (fg('jfp-magma-starttransition-hydrateroot')) {
			// We wrap hydrateRoot in startTransition, in order to prevent state updates during hydration from breaking hydration. Instead deferring them to post-hydration.
			startTransition(() => {
				hydrateRoot(container, children, hydrationOptions);
			});
		} else {
			// hydrateRoot also enables concurrent mode, if you enable hydration on your route. It's also assumed you want and understand concurrent mode.
			hydrateRoot(container, children, hydrationOptions);
		}
	} else {
		// If the route is not enabled for hydration but concurrent mode is enabled for this environment, render in concurrent mode
		const root = createRoot(container);
		root.render(children);
	}
};

const activateReactPatches = () => {
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	window.__ATL_SKIP_HAS_CONTEXT_CHANGED = true;
	if (fg('jfp-magma-enable-patch-capture-hydration-errors')) {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.__ATL_CAPTURE_HYDRATION_ERROR_DETAILS = true;
	}
};

declare global {
	interface Window {
		__ATL_SKIP_HAS_CONTEXT_CHANGED: boolean;
		__ATL_CAPTURE_HYDRATION_ERROR_DETAILS: boolean;
	}
}
